import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import Input from "@/components/shared/ui/Input";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";
import type { Product } from "@/api/features/products/types";

export type AffiliatePartnerFormValues = {
  companyName: string;
  website: string;
  gstNumber: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  city: string;
  state: string;
  country: string;
  notes: string;
  commissionPercent: string;
  products: number[];
};

export function createEmptyAffiliatePartnerForm(): AffiliatePartnerFormValues {
  return {
    companyName: "",
    website: "",
    gstNumber: "",
    contactFirstName: "",
    contactLastName: "",
    contactEmail: "",
    contactPhone: "",
    city: "",
    state: "",
    country: "",
    notes: "",
    commissionPercent: "",
    products: [],
  };
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-gray-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </div>
      {children}
    </label>
  );
}

export default function AffiliatePartnerForm({
  title,
  submitLabel,
  value,
  products,
  loadingProducts,
  submitting,
  errorMessage,
  onChange,
  onSubmit,
  onCancel,
  footerActions,
}: {
  title: string;
  submitLabel: string;
  value: AffiliatePartnerFormValues;
  products: Product[];
  loadingProducts?: boolean;
  submitting?: boolean;
  errorMessage?: string | null;
  onChange: (next: AffiliatePartnerFormValues) => void;
  onSubmit: (value: AffiliatePartnerFormValues) => void;
  onCancel: () => void;
  footerActions?: ReactNode;
}) {
  const [local, setLocal] = useState<AffiliatePartnerFormValues>(value);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const update = <K extends keyof AffiliatePartnerFormValues>(
    key: K,
    nextValue: AffiliatePartnerFormValues[K]
  ) => {
    const next = { ...local, [key]: nextValue };
    setLocal(next);
    onChange(next);
  };

  const toggleProduct = (productId: number) => {
    const next = local.products.includes(productId)
      ? local.products.filter((id) => id !== productId)
      : [...local.products, productId];
    update("products", next);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(local);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Company Name" required>
          <Input
            value={local.companyName}
            onChange={(e) => update("companyName", e.target.value)}
            placeholder="Enter company name"
          />
        </Field>

        <Field label="Website">
          <Input
            value={local.website}
            onChange={(e) => update("website", e.target.value)}
            placeholder="https://example.com"
          />
        </Field>

        <Field label="GST Number">
          <Input
            value={local.gstNumber}
            onChange={(e) => update("gstNumber", e.target.value)}
            placeholder="Enter GST number"
          />
        </Field>

        <Field label="First Name" required>
          <Input
            value={local.contactFirstName}
            onChange={(e) => update("contactFirstName", e.target.value)}
            placeholder="Enter first name"
          />
        </Field>

        <Field label="Last Name" required>
          <Input
            value={local.contactLastName}
            onChange={(e) => update("contactLastName", e.target.value)}
            placeholder="Enter last name"
          />
        </Field>

        <Field label="Email" required>
          <Input
            type="email"
            value={local.contactEmail}
            onChange={(e) => update("contactEmail", e.target.value)}
            placeholder="Enter email address"
          />
        </Field>

        <Field label="Phone">
          <Input
            value={local.contactPhone}
            onChange={(e) => update("contactPhone", e.target.value)}
            placeholder="Enter phone number"
          />
        </Field>

        {/* Country → City cascading selects (state auto-fills from the city),
            matching the User and Supplier forms. */}
        <div className="md:col-span-2">
          <LocationRow
            value={{
              country: local.country,
              state: local.state,
              city: local.city,
            }}
            onChange={(v: CSCFormValue) => {
              const next = {
                ...local,
                country: v.country || "",
                state: v.state || "",
                city: v.city || "",
              };
              setLocal(next);
              onChange(next);
            }}
            instancePrefix="affiliate-partner-csc"
          />
        </div>

        <Field label="Commission Percent">
          <Input
            type="number"
            value={local.commissionPercent}
            onChange={(e) => update("commissionPercent", e.target.value)}
            placeholder="0.00"
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          value={local.notes}
          onChange={(e) => update("notes", e.target.value)}
          rows={5}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          placeholder="Add internal notes"
        />
      </Field>

      <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">Choose the products this affiliate can access.</p>
          </div>
          {loadingProducts ? <span className="text-xs text-gray-500">Loading products…</span> : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => {
            const checked = local.products.includes(product.id);
            return (
              <label
                key={product.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition ${
                  checked
                    ? "border-sky-300 bg-sky-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleProduct(product.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">{product.name}</div>
                  <div className="text-xs text-gray-500">{product.slug}</div>
                </div>
              </label>
            );
          })}

          {!loadingProducts && products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
              No products available.
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
        <div>{footerActions}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
